import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Button, Callout, EmptyState, useSidePanel } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { PageHeader } from 'components/header/PageHeader'
import { useLegalAgreementsQuery } from 'hooks/queries/useLegalAgreementsQuery'
import { LegalAgreementDetails } from './LegalAgreementDetails'
import { LegalAgreementsTable } from './LegalAgreementsTable'
import {
  LegalAgreementStatus,
  LegalAgreementStatusTag,
  LegalAgreementStatusType,
  LegalAgreementTableType,
  LegalAgreementType,
} from './types'

export const LegalAgreements = () => {
  const { t } = useTranslation()
  const { enableLegalAgreementsCallout } = useFeatureFlags()
  const getStatus = (status: LegalAgreementStatusType): LegalAgreementStatusTag => {
    switch (status) {
      case LegalAgreementStatus.PENDING:
        return {
          state: 'warning',
          label: `${t('statusLabel.pending')}`,
        }
      case LegalAgreementStatus.SIGNED:
        return {
          state: 'success',
          label: `${t('statusLabel.accepted')}`,
        }
    }
  }

  const mapAgreementsData = (data: LegalAgreementType[]): LegalAgreementTableType[] => {
    if (!data?.length) return []

    return data?.map(agreement => ({
      ...agreement,
      status: agreement?.status as LegalAgreementStatusType,
      statusPill: getStatus(agreement?.status as LegalAgreementStatusType),
    }))
  }

  const {
    data: agreementsData,
    isFetching: isFetchingAgreementsData,
    isError: isAgreementsDataError,
    refetch: refetchAgreementsData,
  } = useLegalAgreementsQuery([LegalAgreementStatus.PENDING, LegalAgreementStatus.SIGNED])

  const agreementsList = useMemo(() => mapAgreementsData(agreementsData), [agreementsData])

  const {
    isSidePanelOpen: isAgreementDetailsOpen,
    sidePanelData: agreementDetails,
    paging: pageAgreements,
    setIsSidePanelOpen: setIsAgreementDetailsOpen,
    updateSidePanel: updateAgreementDetails,
  } = useSidePanel<LegalAgreementTableType>(agreementsList)

  const showAgreements = useMemo(
    () => !isFetchingAgreementsData && !isAgreementsDataError && !!agreementsData?.length,
    [isFetchingAgreementsData, isAgreementsDataError, agreementsData]
  )
  const showEmptyState = useMemo(
    () => !isFetchingAgreementsData && !isAgreementsDataError && !agreementsData?.length,
    [isFetchingAgreementsData, isAgreementsDataError, agreementsData]
  )
  const showErrorState = useMemo(
    () => !isFetchingAgreementsData && isAgreementsDataError,
    [isFetchingAgreementsData, isAgreementsDataError]
  )

  return (
    <>
      <PageHeader description={t('legalAgreements.mainDescription')} />

      {!!enableLegalAgreementsCallout?.text && (
        <Box paddingB={24}>
          <Callout>{enableLegalAgreementsCallout?.text}</Callout>
        </Box>
      )}

      {(isFetchingAgreementsData || showAgreements) && (
        <LegalAgreementsTable
          agreementsList={agreementsList as LegalAgreementTableType[]}
          isLoading={isFetchingAgreementsData}
          onOpenAgreementDetails={(index: number) => {
            updateAgreementDetails(index)
            setIsAgreementDetailsOpen(true)
          }}
        />
      )}

      {!isFetchingAgreementsData && showAgreements && (
        <LegalAgreementDetails
          isOpen={isAgreementDetailsOpen}
          agreement={agreementDetails}
          pageAgreements={pageAgreements}
          onClose={() => setIsAgreementDetailsOpen(false)}
        />
      )}

      {showEmptyState && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('legalAgreements.emptyStateHeadline')}
          description={t('legalAgreements.emptyStateDescription')}
        />
      )}

      {showErrorState && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('errorMessages.getErrorTitle')}
          description={
            <>
              {t('errorMessages.errorAction')}{' '}
              <Button
                variant="ghost"
                noPadding
                onClick={() => {
                  refetchAgreementsData()
                }}
              >
                {t('errorMessages.reload')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}
